/**
 * What the approver needs to know before approving an employee absence (K4),
 * the web twin of the management app's absence context card. Each fact comes
 * from an existing endpoint and loads and fails on its own; nothing here blocks
 * the approve button. Mount it only while the approve dialog is open: the
 * three queries start when it mounts.
 *
 * - Vacation left if approved (vacation only): the employee's vacation summary
 *   for the year of the first day. `remaining_days` leaves open requests out,
 *   so a REQUESTED vacation subtracts `pending_days`, which holds this request
 *   and any other open one. The server counts work days and holidays; the
 *   client never counts the request's days itself.
 * - Others out: approved absences of colleagues overlapping the dates.
 * - Coverage: the planned status of each group the employee has a shift in
 *   during the absence (worst day per group), or the whole Kita's groups when
 *   there is no such shift. The plan still counts this employee.
 */

import type { ReactNode } from 'react';
import { useLingui } from '@lingui/react';
import { AlertTriangle } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { CoverageStatusBadge } from '@/components/shifts/CoverageStatusBadge';
import { useEmployeeAbsences } from '@/hooks/useEmployeeAbsences';
import { useCoverageRange } from '@/hooks/useShiftCoverage';
import { useShifts } from '@/hooks/useShifts';
import { useVacationSummary } from '@/hooks/useWorkforce';
import { formatDecimal } from '@/lib/format';
import { cn } from '@/lib/utils';
import { compareCoverageSeverity, CoverageStatus, type GroupCoverage } from '@/types/coverage';
import { AbsenceType, ApprovalStatus, type EmployeeAbsence } from '@/types/employeeAbsences';
import { ShiftStatus } from '@/types/shifts';

/** First names listed before "+N more". */
const MAX_NAMES = 3;

interface FactProps {
  label: ReactNode;
  isLoading: boolean;
  /** `undefined` once loaded means the fact failed: shown as a dash. */
  value: ReactNode | undefined;
  caption?: ReactNode;
  warn?: boolean;
  testId: string;
}

function Fact({ label, isLoading, value, caption, warn, testId }: FactProps) {
  const { i18n } = useLingui();
  let body: ReactNode;
  if (isLoading) {
    body = <Skeleton className="ml-auto h-4 w-24" data-testid={`${testId}-loading`} />;
  } else if (value === undefined) {
    body = (
      <span className="text-muted-foreground">
        <span aria-hidden="true">–</span>
        <span className="sr-only">
          {i18n._({ id: 'absences.approval.context.unavailable', message: 'Not available' })}
        </span>
      </span>
    );
  } else {
    body = (
      <>
        <span
          className={cn(
            'inline-flex items-center justify-end gap-1 font-medium',
            warn && 'text-warning-on-surface'
          )}
        >
          {warn && <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />}
          {value}
        </span>
        {caption ? <span className="block text-xs text-muted-foreground">{caption}</span> : null}
      </>
    );
  }
  return (
    <div className="flex items-start justify-between gap-4" data-testid={testId}>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right">{body}</dd>
    </div>
  );
}

export interface AbsenceApprovalContextProps {
  absence: EmployeeAbsence;
}

export function AbsenceApprovalContext({ absence }: AbsenceApprovalContextProps) {
  const { i18n } = useLingui();
  const fromDate = absence.from_date ?? '';
  const toDate = absence.to_date ?? fromDate;
  const employeeId = absence.employee_id ?? '';
  const isVacation = absence.absence_type?.toUpperCase() === AbsenceType.VACATION;
  const isRequested = absence.approval_status === ApprovalStatus.REQUESTED;

  const vacation = useVacationSummary(isVacation ? employeeId : '', Number(fromDate.slice(0, 4)));
  const others = useEmployeeAbsences({
    fromDate,
    toDate,
    approvalStatus: ApprovalStatus.APPROVED,
    limit: 100,
  });
  const coverage = useCoverageRange(fromDate, toDate);
  const shifts = useShifts(
    { employeeId, fromDate, toDate, limit: 100 },
    { enabled: !!employeeId && !!fromDate }
  );

  // Vacation
  let vacationValue: ReactNode | undefined;
  let vacationCaption: ReactNode | undefined;
  let vacationWarn = false;
  const summary = vacation.data;
  if (summary) {
    const pending = summary.pending_days ?? 0;
    const left = (summary.remaining_days ?? 0) - (isRequested ? pending : 0);
    const count = formatDecimal(Math.abs(left), i18n.locale, Number.isInteger(left) ? 0 : 1);
    vacationWarn = left < 0;
    vacationValue =
      left < 0
        ? i18n._({
            id: 'absences.approval.context.vacationOver',
            message: '{n, plural, one {{count} day over} other {{count} days over}}',
            values: { n: Math.abs(left), count },
          })
        : i18n._({
            id: 'absences.approval.context.vacationDays',
            message: '{n, plural, one {{count} day} other {{count} days}}',
            values: { n: left, count },
          });
    if (isRequested && pending > 0) {
      vacationCaption = i18n._({
        id: 'absences.approval.context.vacationPending',
        message:
          '{n, plural, one {Counts all open requests: {count} day} other {Counts all open requests: {count} days}}',
        values: { n: pending, count: formatDecimal(pending, i18n.locale, Number.isInteger(pending) ? 0 : 1) },
      });
    }
  }

  // Colleagues out: one entry per colleague, however many absences they have.
  let othersValue: ReactNode | undefined;
  let othersCaption: ReactNode | undefined;
  if (others.data) {
    const firstNames = new Map<string, string>();
    for (const other of others.data.absences ?? []) {
      if (other.id === absence.id || !other.employee_id || other.employee_id === employeeId) continue;
      // The list filter is by range; keep only real overlaps with this absence.
      if ((other.to_date ?? '') < fromDate || (other.from_date ?? '') > toDate) continue;
      if (!firstNames.has(other.employee_id)) {
        firstNames.set(other.employee_id, other.employee?.firstname?.trim() ?? '');
      }
    }
    const count = firstNames.size;
    if (count === 0) {
      othersValue = i18n._({ id: 'absences.approval.context.colleaguesNone', message: 'Nobody' });
    } else {
      othersValue = i18n._({
        id: 'absences.approval.context.colleaguesCount',
        message: '{count, plural, one {# colleague} other {# colleagues}}',
        values: { count },
      });
      const names = [...firstNames.values()].filter(Boolean);
      const shown = names.slice(0, MAX_NAMES).join(', ');
      const more = count - Math.min(names.length, MAX_NAMES);
      othersCaption = shown
        ? more > 0
          ? i18n._({
              id: 'absences.approval.context.namesAndMore',
              message: '{names} +{count} more',
              values: { names: shown, count: more },
            })
          : shown
        : undefined;
    }
  }

  // Coverage: the worst day per group across the absence.
  let coverageValue: ReactNode | undefined;
  let coverageCaption: ReactNode | undefined;
  let coverageWarn = false;
  if (coverage.data) {
    const worstByGroup = new Map<string, GroupCoverage>();
    for (const day of coverage.data.days ?? []) {
      for (const group of day.groups ?? []) {
        if (!group.group_id) continue;
        const current = worstByGroup.get(group.group_id);
        if (!current || compareCoverageSeverity(group.status, current.status) < 0) {
          worstByGroup.set(group.group_id, group);
        }
      }
    }
    const rosterGroupIds = new Set(
      (shifts.data?.shifts ?? [])
        .filter((s) => s.status !== ShiftStatus.CANCELLED)
        .flatMap((s) => (s.groups ?? []).map((g) => g.id))
        .filter((id): id is string => !!id)
    );
    const affected = [...worstByGroup.values()].filter((g) => rosterGroupIds.has(g.group_id ?? ''));
    if (affected.length > 0) {
      affected.sort((a, b) => compareCoverageSeverity(a.status, b.status));
      coverageWarn = affected.some((g) => compareCoverageSeverity(g.status, CoverageStatus.WARNING) <= 0);
      coverageValue = (
        <span className="flex flex-col items-end gap-1">
          {affected.map((g) => (
            <span key={g.group_id} className="inline-flex items-center gap-2">
              <span className="truncate">{g.group_name}</span>
              <CoverageStatusBadge status={g.status} />
            </span>
          ))}
        </span>
      );
      coverageCaption = i18n._({
        id: 'absences.approval.context.coveragePlanned',
        message: 'Worst day of the plan, still counting this person',
      });
    } else {
      const all = [...worstByGroup.values()];
      const critical = all.filter((g) => g.status === CoverageStatus.CRITICAL).length;
      const warning = all.filter((g) => g.status === CoverageStatus.WARNING).length;
      const ok = all.filter((g) => g.status === CoverageStatus.SUFFICIENT).length;
      coverageWarn = critical + warning > 0;
      coverageValue =
        critical + warning === 0
          ? i18n._({ id: 'absences.approval.context.coverageAllOk', message: 'All groups OK' })
          : i18n._({
              id: 'absences.approval.context.coverageSummary',
              message: '{critical} critical · {warning} tight · {ok} OK',
              values: { critical, warning, ok },
            });
      coverageCaption = i18n._({ id: 'absences.approval.context.allGroups', message: 'All groups' });
    }
  }
  // Wait for the roster unless it failed: a failed lookup falls back to the summary.
  const coverageLoading = coverage.isLoading || (shifts.isLoading && !shifts.error);

  return (
    <dl className="space-y-3 rounded-md border bg-muted/40 p-3 text-sm" data-testid="absence-approval-context">
      {isVacation && (
        <Fact
          testId="absence-context-vacation"
          label={
            isRequested
              ? i18n._({ id: 'absences.approval.context.vacationIfApproved', message: 'Vacation left if approved' })
              : i18n._({ id: 'absences.approval.context.vacationLeft', message: 'Vacation left' })
          }
          isLoading={vacation.isLoading}
          value={vacation.isError ? undefined : vacationValue}
          caption={vacationCaption}
          warn={vacationWarn}
        />
      )}
      <Fact
        testId="absence-context-colleagues"
        label={i18n._({ id: 'absences.approval.context.colleagues', message: 'Others out' })}
        isLoading={others.isLoading}
        value={others.isError ? undefined : othersValue}
        caption={othersCaption}
      />
      <Fact
        testId="absence-context-coverage"
        label={i18n._({ id: 'absences.approval.context.coverage', message: 'Group coverage' })}
        isLoading={coverageLoading}
        value={coverage.isError ? undefined : coverageValue}
        caption={coverageCaption}
        warn={coverageWarn}
      />
    </dl>
  );
}
